import * as React from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button, type ButtonProps } from "./button";

interface CopyButtonProps extends Omit<ButtonProps, "onClick" | "children"> {
  value: string;
  label: string;
  copiedLabel: string;
}

/** Icon button that copies `value` and confirms with a check for two seconds. */
function CopyButton({
  value,
  label,
  copiedLabel,
  className,
  size = "icon-sm",
  variant = "ghost",
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      aria-label={copied ? copiedLabel : label}
      title={copied ? copiedLabel : label}
      className={cn("text-text-tertiary hover:text-foreground", className)}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          /* clipboard unavailable (insecure context) — silently ignore */
        }
      }}
      {...props}
    >
      {copied ? (
        <Check className="size-4 text-status-success-dot animate-scale-in" />
      ) : (
        <Copy className="size-4" />
      )}
    </Button>
  );
}

export { CopyButton };
